import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Target,
  CheckCircle,
  Clock,
  UserPlus,
  AlertCircle,
  Calendar,
  UserX,
  Settings2,
  ChevronRight,
  MessageCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDateTime } from "../lib/utils";
import { CallLogModal } from "../components/common/CallLogModal";
import { QuickActions } from "../components/common/QuickActions";
import { toast } from "sonner";

export const CREDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [urgentCalls, setUrgentCalls] = useState([]);
  const [callLogContact, setCallLogContact] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [user?.id]);

  const fetchDashboardData = async () => {
    if (!user?.id) return;
    try {
      const data = await apiRequest(`/dashboard/cre/${user.id}`);
      setStats(data);
      // Combine urgent calls
      const urgent = [
        ...(data.due_calls || []).map(c => ({ ...c, urgency: "due" })),
        ...(data.overdue_calls || []).map(c => ({ ...c, urgency: "overdue" })),
      ].slice(0, 10);
      setUrgentCalls(urgent);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (customer, skipDialer = false) => {
    // First open the phone dialer (unless skipped)
    if (!skipDialer && customer.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
    // Show call log modal after a brief delay to allow dialer to open
    setTimeout(() => {
      setCallLogContact({
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        status: customer.status,
        type: "customer",
        branch_id: customer.branch_id,
        tag: customer.tag
      });
    }, skipDialer ? 0 : 800);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-xl"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <div key={i} className="h-20 bg-gray-200 rounded-xl"></div>)}
          </div>
        </div>
      </div>
    );
  }

  const progress = stats?.target_calls > 0 
    ? Math.round((stats?.calls_today / stats?.target_calls) * 100) 
    : 0;

  const getProgressColor = () => {
    if (progress >= 100) return "bg-green-500";
    if (progress >= 75) return "bg-yellow-500";
    return "bg-[#ED1C24]";
  };

  return (
    <div className="space-y-6" data-testid="cre-dashboard">
      {/* Hero: Call Progress Card */}
      <Card className="bento-card overflow-hidden" data-testid="call-progress-card">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Today's Progress
              </p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-bold font-heading tabular-nums">
                  {stats?.calls_today || 0}
                </span>
                <span className="text-lg text-muted-foreground">/ {stats?.target_calls || 0}</span>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              stats?.target_mode === "manual" 
                ? "bg-orange-100 text-orange-800" 
                : "bg-cyan-100 text-cyan-800"
            }`} data-testid="target-type-badge">
              <Settings2 className="w-3 h-3" />
              {stats?.target_mode === "manual" ? "Manual" : "Auto"}
            </span>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{progress}% Complete</span>
              <span>{(stats?.target_calls - stats?.calls_today) || 0} remaining</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${getProgressColor()}`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card 
          className="bento-card cursor-pointer status-card-due"
          onClick={() => navigate("/due")}
          data-testid="stat-due"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Due</p>
                <p className="text-2xl font-bold font-heading tabular-nums">{stats?.due_count || 0}</p>
              </div>
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bento-card cursor-pointer status-card-overdue"
          onClick={() => navigate("/overdue")}
          data-testid="stat-overdue"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Overdue</p>
                <p className="text-2xl font-bold font-heading tabular-nums">{stats?.overdue_count || 0}</p>
              </div>
              <AlertCircle className="w-5 h-5 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bento-card cursor-pointer status-card-delayed"
          onClick={() => navigate("/delayed")}
          data-testid="stat-delayed"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Delayed</p>
                <p className="text-2xl font-bold font-heading tabular-nums">{stats?.delayed_count || 0}</p>
              </div>
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card 
          className="bento-card cursor-pointer status-card-no-show"
          onClick={() => navigate("/no-show")}
          data-testid="stat-no-show"
        >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">No Show</p>
                <p className="text-2xl font-bold font-heading tabular-nums">{stats?.no_show_count || 0}</p>
              </div>
              <UserX className="w-5 h-5 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bento-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-50">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.bookings_today || 0}</p>
                <p className="text-xs text-muted-foreground">Bookings Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-50">
                <Phone className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.pending_calls || 0}</p>
                <p className="text-xs text-muted-foreground">Pending Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-50">
                <UserPlus className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">{stats?.new_leads || 0}</p>
                <p className="text-xs text-muted-foreground">New Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bento-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <Target className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-lg font-bold font-heading tabular-nums">
                  {stats?.calls_today && stats?.bookings_today 
                    ? Math.round((stats.bookings_today / stats.calls_today) * 100) 
                    : 0}%
                </p>
                <p className="text-xs text-muted-foreground">Conversion</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Urgent Calls List */}
      <Card className="bento-card" data-testid="urgent-calls">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-base">Urgent Calls</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/due")}>
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {urgentCalls.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500 opacity-50" />
              No urgent calls at the moment
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {urgentCalls.map((customer) => (
                <div 
                  key={customer.id} 
                  className="flex items-center justify-between py-3 px-4 hover:bg-gray-50/50"
                  data-testid={`urgent-call-${customer.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{customer.name}</p>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                        customer.urgency === "overdue" 
                          ? "bg-orange-100 text-orange-800" 
                          : "bg-yellow-100 text-yellow-800"
                      }`}>
                        {customer.urgency}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground font-mono">{formatPhone(customer.phone)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8 text-green-600 hover:bg-green-50"
                      onClick={() => handleCall(customer)}
                      data-testid={`call-${customer.id}`}
                    >
                      <Phone className="w-4 h-4" />
                    </Button>
                    <a
                      href={`https://wa.me/91${customer.phone?.replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-green-600 hover:bg-green-50"
                      data-testid={`whatsapp-${customer.id}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-8 h-8"
                      onClick={() => navigate(`/customers/${customer.id}`)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Call Log Modal */}
      {callLogContact && (
        <CallLogModal
          open={!!callLogContact}
          onClose={() => setCallLogContact(null)}
          contact={callLogContact}
          onSuccess={() => {
            setCallLogContact(null);
            fetchDashboardData();
          }}
        />
      )}
    </div>
  );
};

export default CREDashboard;
